import { useState, useMemo, useEffect } from 'react'
import { Search, BookOpen, TrendingUp, ShoppingCart, Share2, Globe, BarChart3, Mail, DollarSign, Target, Cpu } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { useLanguage } from '../contexts/LanguageContext'

interface Term {
  english: string
  turkish: string
  russian?: string
  abbreviation?: string
  aliases?: string[]
  slug?: string
  description: {
    tr: string
    en: string
    ru?: string
  }
  formula?: {
    tr: string
    en: string
    ru?: string
  }
  category: string
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const createTermId = (term: Term) => {
  if (term.slug) return term.slug
  const base = term.abbreviation ? `${term.abbreviation}-${term.english}` : term.english
  return slugify(base)
}

const Glossary = () => {
  const { language } = useLanguage()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 30

  const terms: Term[] = [
    // Performance Metrics
    {
      english: 'Return on Ad Spend',
      turkish: 'Reklam Harcaması Getirisi',
      russian: 'Возврат рекламных расходов',
      abbreviation: 'ROAS',
      description: {
        tr: 'Reklam harcamasına karşı elde edilen gelir oranı.',
        en: 'The revenue earned for every dollar spent on advertising.',
        ru: 'Коэффициент доходности рекламных расходов.'
      },
      formula: {
        tr: 'ROAS = Gelir / Reklam Harcaması',
        en: 'ROAS = Revenue / Ad Spend',
        ru: 'ROAS = Доход / Расходы на рекламу'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Return on Investment',
      turkish: 'Yatırım Getirisi',
      russian: 'Возврат инвестиций',
      abbreviation: 'ROI',
      description: {
        tr: 'Yatırılan paranın ne kadar kâr getirdiğini gösteren metrik.',
        en: 'A metric showing how much profit the invested money brings.',
        ru: 'Метрика, показывающая прибыльность инвестиций.'
      },
      formula: {
        tr: 'ROI = ((Gelir - Yatırım) / Yatırım) × 100',
        en: 'ROI = ((Revenue - Investment) / Investment) × 100',
        ru: 'ROI = ((Доход - Инвестиции) / Инвестиции) × 100'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Cost Per Click',
      turkish: 'Tıklama Başına Maliyet',
      russian: 'Цена за клик',
      abbreviation: 'CPC',
      description: {
        tr: 'Reklamın bir kez tıklanması için ödenen maliyet.',
        en: 'The cost paid for one click on the advertisement.',
        ru: 'Стоимость одного клика по рекламе.'
      },
      formula: {
        tr: 'CPC = Reklam Harcaması / Tıklama Sayısı',
        en: 'CPC = Ad Spend / Number of Clicks',
        ru: 'CPC = Расходы на рекламу / Количество кликов'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Cost Per Mille',
      turkish: 'Bin Görüntülenme Başına Maliyet',
      russian: 'Цена за тысячу показов',
      abbreviation: 'CPM',
      description: {
        tr: 'Reklamın bin kez görüntülenmesi için ödenen maliyet.',
        en: 'The cost paid for a thousand views of the advertisement.',
        ru: 'Стоимость тысячи показов рекламы.'
      },
      formula: {
        tr: 'CPM = (Reklam Harcaması / Görüntülenme) × 1000',
        en: 'CPM = (Ad Spend / Impressions) × 1000',
        ru: 'CPM = (Расходы на рекламу / Показы) × 1000'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Click-Through Rate',
      turkish: 'Tıklama Oranı',
      russian: 'Кликабельность',
      abbreviation: 'CTR',
      description: {
        tr: 'Reklamın görüntülenme sayısına göre tıklanma oranı.',
        en: 'The click rate of the advertisement based on the number of views.',
        ru: 'Процент кликов по рекламе относительно показов.'
      },
      formula: {
        tr: 'CTR = (Tıklama / Görüntülenme) × 100',
        en: 'CTR = (Clicks / Impressions) × 100',
        ru: 'CTR = (Клики / Показы) × 100'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Conversion Rate',
      turkish: 'Dönüşüm Oranı',
      russian: 'Конверсия',
      abbreviation: 'CVR',
      description: {
        tr: 'Ziyaretçilerin istenen eylemi (satın alma, form doldurma vb.) gerçekleştirme oranı.',
        en: 'The percentage of visitors who complete a desired action (purchase, form submission, etc.).',
        ru: 'Процент посетителей, которые выполняют желаемое действие (покупка, заполнение формы и т.д.).'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Cost Per Acquisition',
      turkish: 'Kazanım Başına Maliyet',
      abbreviation: 'CPA',
      description: {
        tr: 'Bir müşteri kazanmak veya bir dönüşüm elde etmek için ödenen maliyet. CAC ile benzerdir.',
        en: 'The cost paid to acquire a customer or achieve a conversion. Similar to CAC.'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Customer Acquisition Cost',
      turkish: 'Müşteri Kazanma Maliyeti',
      abbreviation: 'CAC',
      description: {
        tr: 'Yeni bir müşteri kazanmak için harcanan toplam pazarlama maliyeti.',
        en: 'The total marketing cost spent to acquire a new customer.'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Customer Lifetime Value',
      turkish: 'Müşteri Yaşam Boyu Değeri',
      abbreviation: 'LTV',
      aliases: ['Lifetime Value', 'CLV', 'Customer Lifetime Value (CLV)'],
      description: {
        tr: 'Bir müşterinin şirketle ilişkisi boyunca getireceği tahmini toplam gelir.',
        en: 'The estimated total revenue a customer will generate during their relationship with a company.'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Average Order Value',
      turkish: 'Ortalama Sipariş Değeri',
      abbreviation: 'AOV',
      description: {
        tr: 'Bir müşterinin tek seferde ortalama harcadığı tutar.',
        en: 'The average amount a customer spends per order.'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Break-Even ROAS',
      turkish: 'Başabaş ROAS',
      description: {
        tr: 'Kar ve zararın eşit olduğu ROAS değeri. Bu değerin altında zarar, üstünde kar edilir.',
        en: 'The ROAS value where profit and loss are equal. Below this value results in loss, above it results in profit.'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Customer Effort Score',
      turkish: 'Müşteri Çaba Skoru',
      abbreviation: 'CES',
      description: {
        tr: 'Müşterilerin bir işlemi tamamlamak için ne kadar çaba sarf ettiğini ölçen deneyim metriği.',
        en: 'An experience metric that measures how much effort customers need to complete an interaction.'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Net Promoter Score',
      turkish: 'Net Tavsiye Skoru',
      abbreviation: 'NPS',
      description: {
        tr: 'Müşterilerin markayı başkalarına tavsiye etme olasılığını ölçen memnuniyet metriği.',
        en: 'A satisfaction metric that measures how likely customers are to recommend a brand to others.'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Churn Rate',
      turkish: 'Terk Oranı',
      description: {
        tr: 'Belirli bir dönemde kaybedilen müşteri veya abonelerin yüzdesi.',
        en: 'The percentage of customers or subscribers lost during a specific period.'
      },
      formula: {
        tr: 'Churn Rate = (Kaybedilen Müşteri / Dönem Başı Müşteri) × 100',
        en: 'Churn Rate = (Customers Lost / Customers at Start of Period) × 100'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Incrementality',
      turkish: 'Artış Analizi',
      description: {
        tr: 'Bir reklam veya kampanyanın sağladığı ek satış veya dönüşüm etkisini ölçmeye yönelik test yaklaşımı.',
        en: 'A testing approach that measures the additional sales or conversions generated by a campaign versus the holdout group.'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'View-Through Conversion',
      turkish: 'Görüntüleme Kaynaklı Dönüşüm',
      abbreviation: 'VTC',
      description: {
        tr: 'Reklamı görüp tıklamayan ancak belirli süre içinde dönüşüm yapan kullanıcıların ölçümü.',
        en: 'A conversion metric that credits users who saw an ad, didn’t click, but converted within a defined time window.'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Attention Metrics',
      turkish: 'Dikkat Metrikleri',
      aliases: ['Attention Score', 'Viewability'],
      description: {
        tr: 'Reklamın ne kadar süre ve hangi görünürlükte izlendiğini ölçerek gerçek dikkat seviyesini raporlayan metrikler.',
        en: 'Metrics that report the actual attention an ad receives by measuring viewability, time-in-view and interaction.'
      },
      category: 'Performance Metrics'
    },

    // Digital Marketing Channels
    {
      english: 'Search Engine Optimization',
      turkish: 'Arama Motoru Optimizasyonu',
      abbreviation: 'SEO',
      description: {
        tr: 'Web sitesinin arama motoru sonuç sayfalarında daha üst sıralarda görünmesi için yapılan optimizasyon çalışmaları.',
        en: 'The practice of optimizing websites to rank higher in search engine results pages.'
      },
      category: 'Digital Marketing Channels'
    },
    {
      english: 'Search Engine Marketing',
      turkish: 'Arama Motoru Pazarlaması',
      abbreviation: 'SEM',
      description: {
        tr: 'Arama motorlarında ücretli reklamlar aracılığıyla web sitesi trafiği artırma stratejisi.',
        en: 'A strategy to increase website traffic through paid advertisements on search engines.'
      },
      category: 'Digital Marketing Channels'
    },
    {
      english: 'Pay-Per-Click',
      turkish: 'Tıklama Başına Ödeme',
      abbreviation: 'PPC',
      description: {
        tr: 'Reklamverenlerin reklamlarına tıklandığında ödeme yaptığı dijital reklam modeli.',
        en: 'A digital advertising model where advertisers pay when their ads are clicked.'
      },
      category: 'Digital Marketing Channels'
    },
    {
      english: 'Social Media Marketing',
      turkish: 'Sosyal Medya Pazarlaması',
      abbreviation: 'SMM',
      description: {
        tr: 'Sosyal medya platformlarını kullanarak marka bilinirliği artırma ve müşteri kazanma stratejisi.',
        en: 'A strategy to increase brand awareness and acquire customers using social media platforms.'
      },
      category: 'Digital Marketing Channels'
    },
    {
      english: 'Content Marketing',
      turkish: 'İçerik Pazarlaması',
      description: {
        tr: 'Değerli, ilgili ve tutarlı içerik oluşturarak hedef kitleyi çekme ve elde tutma stratejisi.',
        en: 'A strategy to attract and retain a target audience by creating valuable, relevant, and consistent content.'
      },
      category: 'Digital Marketing Channels'
    },
    {
      english: 'Email Marketing',
      turkish: 'E-posta Pazarlaması',
      description: {
        tr: 'E-posta kanalı aracılığıyla müşterilerle iletişim kurma, ürün/hizmet tanıtma ve satış yapma stratejisi.',
        en: 'A strategy to communicate with customers, promote products/services, and make sales through email channels.'
      },
      category: 'Digital Marketing Channels'
    },
    {
      english: 'Affiliate Marketing',
      turkish: 'Satış Ortaklığı Pazarlaması',
      description: {
        tr: 'Üçüncü tarafların (affiliate) ürün/hizmet satışı yapması karşılığında komisyon aldığı pazarlama modeli.',
        en: 'A marketing model where third parties (affiliates) earn commission for selling products/services.'
      },
      category: 'Digital Marketing Channels'
    },
    {
      english: 'Influencer Marketing',
      turkish: 'Etkileyici Pazarlama',
      description: {
        tr: 'Belirli bir alanda takipçisi olan kişileri (influencer) kullanarak marka veya ürün tanıtımı yapma stratejisi.',
        en: 'A strategy to promote brands or products using individuals with followers in specific areas (influencers).'
      },
      category: 'Digital Marketing Channels'
    },
    {
      english: 'Video Marketing',
      turkish: 'Video Pazarlaması',
      description: {
        tr: 'Video içerikleri kullanarak marka bilinirliği artırma ve müşteri kazanma stratejisi.',
        en: 'A strategy to increase brand awareness and acquire customers using video content.'
      },
      category: 'Digital Marketing Channels'
    },

    // E-commerce Terms
    {
      english: 'Cost of Goods Sold',
      turkish: 'Satılan Malın Maliyeti',
      abbreviation: 'COGS',
      description: {
        tr: 'Üretilen veya satılan ürünlerin doğrudan maliyeti. Hammaddeler, işçilik ve üretim maliyetlerini içerir.',
        en: 'The direct costs of producing or selling products. Includes raw materials, labor, and production costs.'
      },
      category: 'E-commerce'
    },
    {
      english: 'Gross Profit',
      turkish: 'Brüt Kâr',
      description: {
        tr: 'Toplam gelirden satılan malların maliyetinin çıkarılmasıyla elde edilen kâr.',
        en: 'Profit obtained by subtracting the cost of goods sold from total revenue.'
      },
      category: 'E-commerce'
    },
    {
      english: 'Net Profit',
      turkish: 'Net Kâr',
      description: {
        tr: 'Tüm giderler (maliyetler, vergiler, faizler vb.) çıkarıldıktan sonra kalan kâr.',
        en: 'The profit remaining after all expenses (costs, taxes, interest, etc.) are deducted.'
      },
      category: 'E-commerce'
    },
    {
      english: 'Shopping Cart Abandonment',
      turkish: 'Sepet Terk',
      description: {
        tr: 'Müşterilerin online alışveriş sepetine ürün ekledikten sonra satın alma işlemini tamamlamadan siteyi terk etmesi.',
        en: 'When customers add items to their online shopping cart but leave the site without completing the purchase.'
      },
      category: 'E-commerce'
    },
    {
      english: 'Marketplace',
      turkish: 'Pazar Yeri',
      description: {
        tr: 'Farklı satıcıların ürünlerini sattığı çevrimiçi platform. Örnek: Amazon, Trendyol, Hepsiburada.',
        en: 'An online platform where different sellers sell their products. Examples: Amazon, Trendyol, Hepsiburada.'
      },
      category: 'E-commerce'
    },
    {
      english: 'Average Revenue Per User',
      turkish: 'Kullanıcı Başına Ortalama Gelir',
      abbreviation: 'ARPU',
      description: {
        tr: 'Toplam gelirin aktif kullanıcı sayısına bölünmesiyle elde edilen metrik.',
        en: 'A metric calculated by dividing total revenue by the number of active users.'
      },
      category: 'E-commerce'
    },
    {
      english: 'Gross Merchandise Value',
      turkish: 'Brüt Ürün Hacmi',
      abbreviation: 'GMV',
      description: {
        tr: 'Pazar yerleri gibi platformlarda belirli dönemde gerçekleşen toplam satış hacmi.',
        en: 'The total sales volume transacted on a platform, such as a marketplace, during a given period.'
      },
      category: 'E-commerce'
    },
    {
      english: 'Dynamic Pricing',
      turkish: 'Dinamik Fiyatlandırma',
      description: {
        tr: 'Talep, stok ve rekabet durumuna göre fiyatların otomatik güncellendiği fiyatlama stratejisi.',
        en: 'A pricing strategy that automatically adjusts prices based on demand, inventory and competition.'
      },
      category: 'E-commerce'
    },

    // Analytics & Tracking
    {
      english: 'Google Analytics',
      turkish: 'Google Analytics',
      description: {
        tr: 'Web sitesi trafiğini ve kullanıcı davranışlarını analiz etmek için kullanılan Google\'ın ücretsiz analiz platformu.',
        en: 'Google\'s free analytics platform used to analyze website traffic and user behavior.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Pixel',
      turkish: 'Piksel',
      description: {
        tr: 'Web sitesine eklenen küçük bir kod parçası. Kullanıcı davranışlarını takip etmek ve dönüşümleri ölçmek için kullanılır.',
        en: 'A small code snippet added to a website. Used to track user behavior and measure conversions.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Conversion Tracking',
      turkish: 'Dönüşüm Takibi',
      description: {
        tr: 'Reklam kampanyalarının ne kadar başarılı olduğunu ölçmek için kullanıcıların istenen eylemleri gerçekleştirmesini takip etme.',
        en: 'Tracking when users complete desired actions to measure how successful advertising campaigns are.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Attribution',
      turkish: 'Atıf',
      description: {
        tr: 'Bir satış veya dönüşümün hangi pazarlama kanalına veya temas noktasına atfedileceğini belirleme süreci.',
        en: 'The process of determining which marketing channel or touchpoint should be credited for a sale or conversion.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'UTM Parameters',
      turkish: 'UTM Parametreleri',
      description: {
        tr: 'URL\'lere eklenen etiketler. Hangi pazarlama kampanyası, kaynağı veya içeriğin trafik getirdiğini takip etmek için kullanılır.',
        en: 'Tags added to URLs. Used to track which marketing campaign, source, or content brought traffic.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Customer Data Platform',
      turkish: 'Müşteri Veri Platformu',
      abbreviation: 'CDP',
      aliases: ['Customer Data Platform (CDP)'],
      description: {
        tr: 'Müşteri verilerini tek bir merkezde toplayıp birleştirerek pazarlama kanallarında kullanılabilir hale getiren teknoloji platformu.',
        en: 'A technology platform that unifies customer data into a single hub and makes it usable across marketing channels.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'First-Party Data',
      turkish: 'Birinci Taraf Veri',
      aliases: ['1st-Party Data'],
      description: {
        tr: 'Markanın müşterilerinden doğrudan topladığı veri. Çerez kısıtlamaları sonrası en değerli veri kaynağıdır.',
        en: 'Data collected directly by a brand from its customers. The most valuable data source in the post-cookie era.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Zero-Party Data',
      turkish: 'Sıfırıncı Taraf Veri',
      aliases: ['0-Party Data', 'Zero Party Data'],
      description: {
        tr: 'Müşterilerin kendi rızasıyla paylaştığı tercihler, niyetler ve profil bilgileri.',
        en: 'Preferences, intents and profile information that customers proactively share with a brand.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Data-Driven Attribution',
      turkish: 'Veri Odaklı Atıf',
      description: {
        tr: 'Makine öğrenimiyle her temas noktasının dönüşüme katkısını hesaplayan atıf modeli.',
        en: 'An attribution model that uses machine learning to assign credit to each touchpoint based on its contribution to the conversion.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Media Mix Modeling',
      turkish: 'Medya Karması Modelleme',
      abbreviation: 'MMM',
      description: {
        tr: 'Tüm pazarlama kanallarının satışa etkisini istatistiksel olarak analiz edip bütçeyi optimize eden model.',
        en: 'A statistical model that quantifies the impact of all marketing channels on sales to guide budget allocation.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Lightweight MMM',
      turkish: 'Hafif MMM',
      aliases: ['Marketing Mix Modeling Lite', 'Lightweight Media Mix Modeling'],
      description: {
        tr: 'Küçük bütçeler ve kısa veri setleriyle hızla uygulanabilen medya karması modelleme yaklaşımı.',
        en: 'A media mix modeling approach designed for small budgets and limited datasets, enabling faster insights.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Incrementality Lift Test',
      turkish: 'Lift Testi',
      description: {
        tr: 'Test ve kontrol gruplarıyla kampanyanın gerçekten ek değer yaratıp yaratmadığını ölçen deney tasarımı.',
        en: 'An experimental design that uses test and control groups to measure the incremental lift generated by a campaign.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Geo-Experiment',
      turkish: 'Coğrafi Deney',
      aliases: ['GeoLift'],
      description: {
        tr: 'Farklı şehir veya bölgeleri test ve kontrol grubu olarak kullanarak kampanya etkisini ölçen deney yöntemi.',
        en: 'An experiment design that measures campaign impact by assigning regions or cities to test and control groups.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Synthetic Control',
      turkish: 'Sentetik Kontrol',
      description: {
        tr: 'Gerçek kontrol grubunun olmadığı durumlarda benzer bölgeleri birleştirerek oluşturulan yapay karşılaştırma grubu.',
        en: 'A method that builds a synthetic comparison group by combining similar regions when no natural control group exists.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Bayesian Modeling',
      turkish: 'Bayesçi Modelleme',
      description: {
        tr: 'Belirsizlikleri olasılıksal olarak hesaba katan, pazarlama ölçümlerinde kullanılan istatistiksel modelleme yaklaşımı.',
        en: 'A statistical modeling approach that incorporates uncertainty probabilistically, often used in marketing measurement.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Multi-Touch Attribution',
      turkish: 'Çoklu Dokunuş Atfı',
      description: {
        tr: 'Dönüşüm yolundaki birden fazla temas noktasına ağırlık vererek krediyi dağıtan atıf yaklaşımı.',
        en: 'An attribution approach that distributes conversion credit across multiple touchpoints along the customer journey.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Last-Click Attribution',
      turkish: 'Son Tıklama Atfı',
      description: {
        tr: 'Dönüşüm kredisi %100 son temas noktasına verilen atıf modeli.',
        en: 'An attribution model that gives 100% conversion credit to the final touchpoint.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Cohort Analysis',
      turkish: 'Kohort Analizi',
      description: {
        tr: 'Benzer özelliklere sahip kullanıcı gruplarının (kohortların) zaman içindeki davranışlarını inceleyen analiz yöntemi.',
        en: 'An analysis method that examines how groups of users with shared characteristics (cohorts) behave over time.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Server-Side Tracking',
      turkish: 'Sunucu Tarafı Takip',
      description: {
        tr: 'Dönüşüm ve etkinlik verilerini tarayıcı yerine sunucu üzerinden göndererek veri kaybını azaltan ölçüm yöntemi.',
        en: 'A measurement method that sends conversion and event data from the server instead of the browser to reduce signal loss.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Meta Conversions API',
      turkish: 'Meta Dönüşümler API',
      abbreviation: 'Meta CAPI',
      description: {
        tr: 'Facebook ve Instagram kampanyaları için sunucu tarafı dönüşüm verisi göndermeye yarayan Meta entegrasyonu.',
        en: 'Meta’s integration that enables server-side sharing of conversion data for Facebook and Instagram campaigns.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Google Enhanced Conversions',
      turkish: 'Google Gelişmiş Dönüşümler',
      description: {
        tr: 'Google Ads kampanyalarında eşleşme oranını artırmak için müşteri verilerini hashleyip Google’a ileten ölçüm özelliği.',
        en: 'A Google Ads measurement feature that hashes customer data to improve conversion matching accuracy.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Event Streaming',
      turkish: 'Olay Akışı',
      description: {
        tr: 'Gerçek zamanlı kullanıcı ve işlem verilerini Kafka gibi platformlarla farklı sistemlere ileten veri akış mimarisi.',
        en: 'A data pipeline architecture that delivers real-time user and transaction events to downstream systems via platforms like Kafka.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Journey Orchestration',
      turkish: 'Yolculuk Orkestrasyonu',
      description: {
        tr: 'Müşteri davranışlarına göre temas noktalarını otomatik tetikleyip kişiselleştiren pazarlama akışları.',
        en: 'Marketing flows that personalize and trigger touchpoints automatically based on customer behavior.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Predictive Audiences',
      turkish: 'Tahmine Dayalı Kitleler',
      description: {
        tr: 'Makine öğrenimiyle dönüşüm olasılığı yüksek kullanıcıları öngörüp hedeflemeye yarayan Google Analytics 4 özelliği.',
        en: 'A Google Analytics 4 feature that uses machine learning to predict and target users likely to convert.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Privacy Sandbox',
      turkish: 'Privacy Sandbox',
      description: {
        tr: 'Google’ın çerezsiz reklam ölçümüne geçiş için geliştirdiği, Topics API gibi çözümleri içeren gizlilik girişimi.',
        en: 'Google’s privacy initiative that includes solutions like the Topics API to support advertising without third-party cookies.'
      },
      category: 'Analytics & Tracking'
    },
    {
      english: 'Topics API',
      turkish: 'Topics API',
      description: {
        tr: 'Üçüncü taraf çerezler olmadan kullanıcı ilgi alanlarını kategoriler halinde paylaşmayı sağlayan Privacy Sandbox bileşeni.',
        en: 'A Privacy Sandbox component that categorizes and shares user interests without third-party cookies.'
      },
      category: 'Analytics & Tracking'
    },

    // Ad Platforms
    {
      english: 'Google Ads',
      turkish: 'Google Ads',
      description: {
        tr: 'Google\'ın ücretli arama ve görüntülü reklam platformu. Önceden Google AdWords olarak biliniyordu.',
        en: 'Google\'s paid search and display advertising platform. Previously known as Google AdWords.'
      },
      category: 'Ad Platforms'
    },
    {
      english: 'Performance Max',
      turkish: 'Performance Max',
      abbreviation: 'PMax',
      aliases: ['PMax Kampanyası', 'Performance Max Campaigns'],
      description: {
        tr: 'Google Ads\'te tüm kanalları (Arama, YouTube, Discover, Gmail, Haritalar) tek kampanyada optimize eden makine öğrenimi tabanlı kampanya türü.',
        en: 'A machine-learning powered Google Ads campaign type that optimizes across all channels (Search, YouTube, Discover, Gmail, Maps) within a single campaign.'
      },
      category: 'Ad Platforms'
    },
    {
      english: 'Demand Gen Ads',
      turkish: 'Demand Gen Reklamları',
      aliases: ['Demand Generation Ads', 'Google Demand Gen'],
      description: {
        tr: 'Google Ads\'in keşif ve görsel ağırlıklı kanallarda yeni talep oluşturmak için geliştirdiği reklam türü. Discovery kampanyalarının yerini alır.',
        en: 'A Google Ads campaign type designed to generate new demand across discovery- and visual-first surfaces, replacing Discovery campaigns.'
      },
      category: 'Ad Platforms'
    },
    {
      english: 'Retail Media',
      turkish: 'Perakende Medyası',
      description: {
        tr: 'Trendyol, Amazon gibi perakende platformlarının kendi reklam envanterlerinde markalara sunduğu performans reklamları.',
        en: 'Advertising solutions offered by retail platforms such as Trendyol or Amazon on their own properties.'
      },
      category: 'Ad Platforms'
    },
    {
      english: 'Programmatic Display',
      turkish: 'Programatik Görüntülü Reklam',
      aliases: ['RTB', 'Real-Time Bidding'],
      description: {
        tr: 'Reklam envanterinin gerçek zamanlı açık artırma ile otomatik satın alındığı dijital reklam modeli.',
        en: 'A digital advertising model where inventory is bought automatically via real-time bidding.'
      },
      category: 'Ad Platforms'
    },
    {
      english: 'Connected TV Ads',
      turkish: 'Bağlı TV Reklamları',
      abbreviation: 'CTV Ads',
      description: {
        tr: 'Akıllı televizyon ve OTT platformlarında programatik olarak yayınlanan video reklamlar.',
        en: 'Programmatic video ads delivered on smart TVs and OTT platforms.'
      },
      category: 'Ad Platforms'
    },
    {
      english: 'TikTok Spark Ads',
      turkish: 'TikTok Spark Reklamları',
      aliases: ['Spark Ads'],
      description: {
        tr: 'TikTok kreatörlerinin organik videolarını sponsorlu olarak yeniden hedeflemeye yarayan reklam formatı.',
        en: 'A TikTok ad format that boosts creators’ organic videos as paid placements.'
      },
      category: 'Ad Platforms'
    },
    {
      english: 'Shopify Collabs',
      turkish: 'Shopify İşbirlikleri',
      description: {
        tr: 'Shopify’ın influencer ve içerik üreticileriyle marka işbirliklerini yöneten pazar yeri platformu.',
        en: 'Shopify’s marketplace platform that manages collaborations between brands and creators.'
      },
      category: 'Ad Platforms'
    },
    {
      english: 'Meta Ads',
      turkish: 'Meta Reklamları',
      description: {
        tr: 'Facebook, Instagram, WhatsApp ve Messenger platformlarında reklam verme platformu.',
        en: 'The advertising platform for Facebook, Instagram, WhatsApp, and Messenger.'
      },
      category: 'Ad Platforms'
    },
    {
      english: 'Facebook Ads',
      turkish: 'Facebook Reklamları',
      description: {
        tr: 'Facebook platformunda gösterilen ücretli reklamlar.',
        en: 'Paid advertisements displayed on the Facebook platform.'
      },
      category: 'Ad Platforms'
    },
    {
      english: 'Instagram Ads',
      turkish: 'Instagram Reklamları',
      description: {
        tr: 'Instagram platformunda gösterilen ücretli reklamlar.',
        en: 'Paid advertisements displayed on the Instagram platform.'
      },
      category: 'Ad Platforms'
    },

    // Targeting & Audience
    {
      english: 'Target Audience',
      turkish: 'Hedef Kitle',
      description: {
        tr: 'Pazarlama mesajlarının ulaştırıldığı belirli bir grup insan. Yaş, cinsiyet, ilgi alanları, davranışlar gibi kriterlere göre belirlenir.',
        en: 'A specific group of people to whom marketing messages are delivered. Determined by criteria such as age, gender, interests, behaviors.'
      },
      category: 'Targeting & Audience'
    },
    {
      english: 'Lookalike Audience',
      turkish: 'Benzer Kitle',
      description: {
        tr: 'Mevcut müşterilerinize benzer özelliklere sahip yeni kullanıcıları bulmak için oluşturulan hedef kitle.',
        en: 'A target audience created to find new users with similar characteristics to your existing customers.'
      },
      category: 'Targeting & Audience'
    },
    {
      english: 'Retargeting',
      turkish: 'Yeniden Hedefleme',
      description: {
        tr: 'Web sitenizi ziyaret etmiş ancak satın alma yapmamış kullanıcılara tekrar reklam gösterilmesi stratejisi.',
        en: 'A strategy to show ads again to users who visited your website but did not make a purchase.'
      },
      category: 'Targeting & Audience'
    },
    {
      english: 'Demographic Targeting',
      turkish: 'Demografik Hedefleme',
      description: {
        tr: 'Yaş, cinsiyet, eğitim, gelir düzeyi gibi demografik özelliklere göre reklam hedefleme.',
        en: 'Targeting advertisements based on demographic characteristics such as age, gender, education, income level.'
      },
      category: 'Targeting & Audience'
    },
    {
      english: 'Behavioral Targeting',
      turkish: 'Davranışsal Hedefleme',
      description: {
        tr: 'Kullanıcıların online davranışlarına (ziyaret ettiği siteler, tıkladığı bağlantılar vb.) göre reklam hedefleme.',
        en: 'Targeting advertisements based on users\' online behaviors (sites visited, links clicked, etc.).'
      },
      category: 'Targeting & Audience'
    },

    // Content & SEO
    {
      english: 'Keyword',
      turkish: 'Anahtar Kelime',
      description: {
        tr: 'Arama motorlarında kullanıcıların aradığı kelime veya kelime grupları. SEO ve reklam kampanyalarında kullanılır.',
        en: 'Words or phrases that users search for in search engines. Used in SEO and advertising campaigns.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Long-Tail Keyword',
      turkish: 'Uzun Kuyruk Anahtar Kelime',
      description: {
        tr: '3-4 kelimeden oluşan, daha spesifik ve daha az rekabetli anahtar kelimeler.',
        en: '3-4 word keywords that are more specific and less competitive.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Search Volume',
      turkish: 'Arama Hacmi',
      description: {
        tr: 'Belirli bir anahtar kelimenin arama motorlarında ne kadar sıklıkla arandığını gösteren metrik.',
        en: 'A metric showing how frequently a specific keyword is searched in search engines.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Keyword Difficulty',
      turkish: 'Anahtar Kelime Zorluğu',
      description: {
        tr: 'Belirli bir anahtar kelime için arama motoru sonuç sayfalarında üst sıralarda yer almanın zorluk derecesi.',
        en: 'The difficulty level of ranking in the top positions of search engine results for a specific keyword.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'On-Page SEO',
      turkish: 'Sayfa İçi SEO',
      description: {
        tr: 'Web sitesinin içeriğinde ve HTML yapısında yapılan SEO optimizasyonları.',
        en: 'SEO optimizations made in the website\'s content and HTML structure.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Off-Page SEO',
      turkish: 'Sayfa Dışı SEO',
      description: {
        tr: 'Web sitesinin dışında yapılan SEO çalışmaları. Backlink, sosyal sinyaller, referanslar gibi.',
        en: 'SEO work done outside the website. Includes backlinks, social signals, referrals, etc.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Backlink',
      turkish: 'Geri Bağlantı',
      description: {
        tr: 'Başka bir web sitesinden sizin web sitenize verilen bağlantı. SEO için önemli bir faktördür.',
        en: 'A link from another website to your website. An important factor for SEO.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Meta Description',
      turkish: 'Meta Açıklama',
      description: {
        tr: 'Arama motoru sonuç sayfalarında web sitesi başlığının altında görünen kısa açıklama metni.',
        en: 'The short description text that appears below the website title in search engine results pages.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Title Tag',
      turkish: 'Başlık Etiketi',
      description: {
        tr: 'Web sayfasının HTML başlığı. Arama motoru sonuç sayfalarında ve tarayıcı sekmesinde görünür.',
        en: 'The HTML title of a web page. Visible in search engine results pages and browser tabs.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'E-E-A-T',
      turkish: 'Deneyim, Uzmanlık, Otorite, Güven',
      description: {
        tr: 'Google’ın içerik kalitesi değerlendirmesinde kullandığı Deneyim (Experience), Uzmanlık (Expertise), Otorite (Authority) ve Güven (Trust) prensipleri.',
        en: 'Google’s content quality framework focusing on Experience, Expertise, Authority and Trust.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Programmatic SEO',
      turkish: 'Programatik SEO',
      description: {
        tr: 'Binlerce long-tail sayfayı şablon ve veri setleriyle otomatik üretip optimize etme yaklaşımı.',
        en: 'An approach that programmatically creates and optimizes thousands of long-tail pages using templates and data sets.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Schema Markup',
      turkish: 'Schema İşaretlemesi',
      description: {
        tr: 'Arama motorlarının içeriği daha iyi anlaması için sayfalara eklenen yapılandırılmış veri işaretlemeleri.',
        en: 'Structured data annotations added to pages to help search engines better understand content.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Topical Authority',
      turkish: 'Konu Otoritesi',
      description: {
        tr: 'Bir web sitesinin belli bir konu alanında kapsamlı içerik üreterek uzmanlık sinyali oluşturması.',
        en: 'The perceived expertise a site builds by publishing comprehensive content around a specific topic.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Content Cluster',
      turkish: 'İçerik Kümesi',
      description: {
        tr: 'Ana bir pillar içerik etrafında ilgili alt içerikleri gruplayarak otorite oluşturan SEO stratejisi.',
        en: 'An SEO strategy that groups related subtopics around a central pillar piece to build authority.'
      },
      category: 'Content & SEO'
    },
    {
      english: 'Pillar Page',
      turkish: 'Pillar Sayfası',
      description: {
        tr: 'Belirli bir konuyu geniş kapsamlı ele alan ve alt içeriklere bağlantı veren ana referans sayfa.',
        en: 'A comprehensive page covering a core topic in depth and linking out to related cluster content.'
      },
      category: 'Content & SEO'
    },

    // Social Media
    {
      english: 'Engagement Rate',
      turkish: 'Etkileşim Oranı',
      description: {
        tr: 'Sosyal medya gönderilerinin aldığı etkileşimlerin (beğeni, yorum, paylaşım) takipçi sayısına oranı.',
        en: 'The ratio of interactions (likes, comments, shares) received by social media posts to the number of followers.'
      },
      category: 'Social Media'
    },
    {
      english: 'Reach',
      turkish: 'Erişim',
      description: {
        tr: 'Sosyal medya gönderisinin kaç farklı kullanıcıya ulaştığını gösteren metrik.',
        en: 'A metric showing how many different users a social media post reached.'
      },
      category: 'Social Media'
    },
    {
      english: 'Impressions',
      turkish: 'Görüntülenme',
      description: {
        tr: 'Bir gönderinin veya reklamın kaç kez görüntülendiğini gösteren metrik.',
        en: 'A metric showing how many times a post or advertisement was viewed.'
      },
      category: 'Social Media'
    },
    {
      english: 'User-Generated Content',
      turkish: 'Kullanıcı Üretimi İçerik',
      abbreviation: 'UGC',
      description: {
        tr: 'Markaların kullanıcıları tarafından oluşturulan içerik. Genellikle sosyal medyada paylaşılan fotoğraf, video veya yorumlar.',
        en: 'Content created by brand users. Usually photos, videos, or comments shared on social media.'
      },
      category: 'Social Media'
    },

    // Marketing Strategy
    {
      english: 'Marketing Funnel',
      turkish: 'Pazarlama Hunisi',
      description: {
        tr: 'Müşteri yolculuğunu farklı aşamalara (farkındalık, ilgi, değerlendirme, satın alma) ayıran pazarlama modeli.',
        en: 'A marketing model that divides the customer journey into different stages (awareness, interest, evaluation, purchase).'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'A/B Testing',
      turkish: 'A/B Testi',
      description: {
        tr: 'İki farklı versiyonu (A ve B) karşılaştırarak hangisinin daha iyi performans gösterdiğini belirleme yöntemi.',
        en: 'A method to determine which performs better by comparing two different versions (A and B).'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Landing Page',
      turkish: 'Açılış Sayfası',
      description: {
        tr: 'Reklam veya kampanyadan gelen ziyaretçilerin yönlendirildiği özel olarak tasarlanmış sayfa.',
        en: 'A specially designed page where visitors from ads or campaigns are directed.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Call-to-Action',
      turkish: 'Harekete Geçirme',
      abbreviation: 'CTA',
      description: {
        tr: 'Kullanıcıları belirli bir eyleme yönlendiren buton veya metin. Örnek: "Şimdi Satın Al", "İndir", "Kayıt Ol".',
        en: 'A button or text that directs users to take a specific action. Examples: "Buy Now", "Download", "Sign Up".'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Brand Awareness',
      turkish: 'Marka Bilinirliği',
      description: {
        tr: 'Hedef kitlenin bir markayı ne kadar tanıdığını ve hatırladığını ölçen metrik.',
        en: 'A metric measuring how well a target audience knows and remembers a brand.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Lead Generation',
      turkish: 'Potansiyel Müşteri Kazanımı',
      description: {
        tr: 'Ürün veya hizmetle ilgilenen potansiyel müşterileri tanımlama ve bilgilerini toplama süreci.',
        en: 'The process of identifying and collecting information from potential customers interested in a product or service.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Customer Journey',
      turkish: 'Müşteri Yolculuğu',
      description: {
        tr: 'Bir müşterinin markayla ilk temasından satın alma ve sadakat aşamasına kadar geçen süreç.',
        en: 'The process from a customer\'s first contact with a brand to purchase and loyalty stages.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Conversion Rate Optimization',
      turkish: 'Dönüşüm Oranı Optimizasyonu',
      abbreviation: 'CRO',
      description: {
        tr: 'Web sitesinde veya kampanyalarda dönüşüm oranını artırmak için yapılan sürekli test ve iyileştirme süreci.',
        en: 'The continuous process of testing and improving websites or campaigns to increase conversion rate.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Marketing Automation',
      turkish: 'Pazarlama Otomasyonu',
      description: {
        tr: 'Kampanyaları, müşteri iletişimini ve tekrar eden pazarlama görevlerini otomatikleştiren yazılım ve süreçler.',
        en: 'Software and processes that automate campaigns, customer communications and recurring marketing tasks.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Lead Scoring',
      turkish: 'Potansiyel Müşteri Skorlama',
      description: {
        tr: 'Potansiyel müşterilere demografik ve davranışsal verilere göre puan vererek satış önceliği belirleme yöntemi.',
        en: 'A method of ranking leads by assigning scores based on demographic and behavioral data to prioritize sales follow-up.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Growth Loop',
      turkish: 'Büyüme Döngüsü',
      description: {
        tr: 'Her yeni kullanıcının daha fazla kullanıcı veya değer yarattığı kendini besleyen büyüme modeli.',
        en: 'A self-reinforcing growth model where each new user creates additional users or value.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'RFM Segmentation',
      turkish: 'RFM Segmentasyonu',
      description: {
        tr: 'Müşterileri alışveriş sıklığı, tutarı ve güncelliğine göre segmentlere ayıran analitik yöntem.',
        en: 'An analytical method that segments customers based on recency, frequency and monetary value.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Product-Led Growth',
      turkish: 'Ürün Odaklı Büyüme',
      abbreviation: 'PLG',
      description: {
        tr: 'Ürünün kendisini ücretsiz deneme, freemium gibi modellerle deneyimleten ve büyümeyi kullanıcı içinden tetikleyen strateji.',
        en: 'A strategy where the product drives acquisition, activation and expansion through models like freemium and free trials.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'North Star Metric',
      turkish: 'Kutup Yıldızı Metrik',
      description: {
        tr: 'Şirketin uzun vadeli değer üretimini en iyi yansıtan tek kilit metrik.',
        en: 'The single key metric that best captures the long-term value a company delivers to customers.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Activation Rate',
      turkish: 'Aktivasyon Oranı',
      description: {
        tr: 'Yeni kullanıcıların ilk değer anına ulaşma, ürünü aktif kullanmaya başlama yüzdesi.',
        en: 'The percentage of new users who reach their first moment of value and become active.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Time to Value',
      turkish: 'Değere Ulaşma Süresi',
      description: {
        tr: 'Bir kullanıcının üründen ilk anlamlı değeri elde etmesi için geçen süre.',
        en: 'The amount of time it takes for a user to experience meaningful value from a product.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Pirate Metrics',
      turkish: 'AARRR Ölçümleri',
      description: {
        tr: 'Acquisition, Activation, Retention, Referral, Revenue aşamalarını izleyen büyüme çerçevesi.',
        en: 'A growth framework tracking Acquisition, Activation, Retention, Referral and Revenue stages.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Growth Experiment',
      turkish: 'Büyüme Deneyi',
      description: {
        tr: 'Hipotez, deney tasarımı ve ölçüm adımlarıyla büyümeyi hızlandırmaya odaklanan test süreçleri.',
        en: 'A structured test process with hypothesis, design and measurement aimed at accelerating growth.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Experiment Backlog',
      turkish: 'Deney Backlogu',
      description: {
        tr: 'Önceliklendirilmiş büyüme testlerinin tutulduğu yol haritası.',
        en: 'A prioritized roadmap of planned growth experiments.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Loyalty Program',
      turkish: 'Sadakat Programı',
      description: {
        tr: 'Müşterilere puan, indirim veya avantaj sunarak tekrar alışverişi teşvik eden programlar.',
        en: 'Programs that reward customers with points, discounts or perks to encourage repeat purchases.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Win-Back Campaign',
      turkish: 'Geri Kazanım Kampanyası',
      description: {
        tr: 'Aktif olmayan veya kaybedilen müşterileri yeniden kazanmaya yönelik hedefli kampanya.',
        en: 'A targeted campaign designed to re-engage inactive or churned customers.'
      },
      category: 'Marketing Strategy'
    },

    // Technical Terms
    {
      english: 'Cookie',
      turkish: 'Çerez',
      description: {
        tr: 'Web sitesinin kullanıcının tarayıcısına kaydettiği küçük veri dosyası. Kullanıcı tercihlerini ve davranışlarını takip etmek için kullanılır.',
        en: 'A small data file that a website saves to a user\'s browser. Used to track user preferences and behaviors.'
      },
      category: 'Technical Terms'
    },
    {
      english: 'API',
      turkish: 'Uygulama Programlama Arayüzü',
      abbreviation: 'API',
      description: {
        tr: 'Farklı yazılım sistemlerinin birbirleriyle iletişim kurmasını sağlayan arayüz.',
        en: 'An interface that allows different software systems to communicate with each other.'
      },
      category: 'Technical Terms'
    },
    {
      english: 'Bounce Rate',
      turkish: 'Hemen Çıkma Oranı',
      description: {
        tr: 'Web sitesine gelen ziyaretçilerin sadece bir sayfayı görüntüleyip siteden ayrılma oranı.',
        en: 'The percentage of visitors who view only one page and then leave the website.'
      },
      category: 'Technical Terms'
    },
    {
      english: 'Session',
      turkish: 'Oturum',
      description: {
        tr: 'Bir kullanıcının web sitesinde geçirdiği zaman dilimi. Genellikle siteye girişten çıkışa kadar olan süre.',
        en: 'The time period a user spends on a website. Usually the time from entering to leaving the site.'
      },
      category: 'Technical Terms'
    },
    {
      english: 'Page Speed',
      turkish: 'Sayfa Hızı',
      description: {
        tr: 'Bir web sayfasının yüklenme süresi. SEO ve kullanıcı deneyimi için önemlidir.',
        en: 'The loading time of a web page. Important for SEO and user experience.'
      },
      category: 'Technical Terms'
    },

    // Additional Marketing Terms
    {
      english: 'Brand Equity',
      turkish: 'Marka Değeri',
      description: {
        tr: 'Bir markanın müşterilerin gözünde sahip olduğu değer ve güç.',
        en: 'The value and power a brand holds in the eyes of customers.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Market Share',
      turkish: 'Pazar Payı',
      description: {
        tr: 'Bir şirketin veya ürünün toplam pazar içindeki satış oranı.',
        en: 'A company\'s or product\'s sales ratio within the total market.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Competitive Analysis',
      turkish: 'Rekabet Analizi',
      description: {
        tr: 'Rakiplerin pazarlama stratejileri, güçlü ve zayıf yönlerini analiz etme süreci.',
        en: 'The process of analyzing competitors\' marketing strategies, strengths, and weaknesses.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'SWOT Analysis',
      turkish: 'SWOT Analizi',
      abbreviation: 'SWOT',
      description: {
        tr: 'Güçlü yönler (Strengths), zayıf yönler (Weaknesses), fırsatlar (Opportunities) ve tehditler (Threats) analizi.',
        en: 'Analysis of Strengths, Weaknesses, Opportunities, and Threats.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'KPI',
      turkish: 'Anahtar Performans Göstergesi',
      abbreviation: 'KPI',
      description: {
        tr: 'Pazarlama kampanyalarının başarısını ölçmek için kullanılan önemli metrikler.',
        en: 'Important metrics used to measure the success of marketing campaigns.'
      },
      category: 'Performance Metrics'
    },
    {
      english: 'Marketing Mix',
      turkish: 'Pazarlama Karması',
      description: {
        tr: 'Ürün (Product), Fiyat (Price), Yer (Place) ve Promosyon (Promotion) olmak üzere 4P modeli.',
        en: 'The 4P model: Product, Price, Place, and Promotion.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Brand Positioning',
      turkish: 'Marka Konumlandırması',
      description: {
        tr: 'Markanın müşterilerin zihninde rakiplerinden farklı bir konumda yer almasını sağlama stratejisi.',
        en: 'A strategy to position a brand differently from competitors in customers\' minds.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Customer Retention',
      turkish: 'Müşteri Elde Tutma',
      description: {
        tr: 'Mevcut müşterileri kaybetmemek ve uzun vadeli ilişkiler kurmak için yapılan çalışmalar.',
        en: 'Efforts to retain existing customers and build long-term relationships.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Cross-Selling',
      turkish: 'Çapraz Satış',
      description: {
        tr: 'Mevcut müşterilere ilgili ürün veya hizmetler satma stratejisi.',
        en: 'A strategy to sell related products or services to existing customers.'
      },
      category: 'Marketing Strategy'
    },
    {
      english: 'Upselling',
      turkish: 'Yukarı Satış',
      description: {
        tr: 'Müşterilere daha pahalı veya daha gelişmiş versiyonlar satma stratejisi.',
        en: 'A strategy to sell more expensive or advanced versions to customers.'
      },
      category: 'Marketing Strategy'
    }
  ]

  const categoryFilters = [
    {
      id: 'all',
      name: { tr: 'Tümü', en: 'All', ru: 'Все' },
      icon: BookOpen
    },
    {
      id: 'Performance Metrics',
      name: { tr: 'ROAS & ROI', en: 'ROAS & ROI', ru: 'ROAS & ROI' },
      icon: TrendingUp
    },
    {
      id: 'E-commerce',
      name: { tr: 'E-Ticaret', en: 'E-commerce', ru: 'Электронная коммерция' },
      icon: ShoppingCart
    },
    {
      id: 'Social Media',
      name: { tr: 'Sosyal Medya', en: 'Social Media', ru: 'Социальные сети' },
      icon: Share2
    },
    {
      id: 'Content & SEO',
      name: { tr: 'SEO', en: 'SEO', ru: 'SEO' },
      icon: Globe
    },
    {
      id: 'Content Marketing',
      name: { tr: 'İçerik', en: 'Content', ru: 'Контент' },
      icon: BookOpen
    },
    {
      id: 'Marketing Strategy',
      name: { tr: 'Strateji', en: 'Strategy', ru: 'Стратегия' },
      icon: Target
    },
    {
      id: 'Analytics & Tracking',
      name: { tr: 'Analitik', en: 'Analytics', ru: 'Аналитика' },
      icon: BarChart3
    },
    {
      id: 'Email Marketing',
      name: { tr: 'E-Posta', en: 'E-Mail', ru: 'Email' },
      icon: Mail
    },
    {
      id: 'Ad Platforms',
      name: { tr: 'Reklamcılık', en: 'Advertising', ru: 'Реклама' },
      icon: DollarSign
    },
    {
      id: 'Technical Terms',
      name: { tr: 'Teknik', en: 'Technical', ru: 'Технические' },
      icon: Cpu
    }
  ]

  const filteredTerms = useMemo(() => {
    return terms.filter(term => {
      let matchesCategory = false
      if (selectedCategory === 'all') {
        matchesCategory = true
      } else if (selectedCategory === 'Content Marketing') {
        // Content Marketing includes both Content Marketing and some Content & SEO terms
        matchesCategory = term.category === 'Content Marketing' || 
          (term.category === 'Digital Marketing Channels' && term.english.toLowerCase().includes('content'))
      } else if (selectedCategory === 'Content & SEO') {
        matchesCategory = term.category === 'Content & SEO'
      } else if (selectedCategory === 'Email Marketing') {
        matchesCategory = term.category === 'Digital Marketing Channels' && term.english.toLowerCase().includes('email')
      } else {
        matchesCategory = term.category === selectedCategory
      }
      
      const query = searchQuery.toLowerCase()
      const matchesSearch = searchQuery === '' || 
        term.english.toLowerCase().includes(query) ||
        term.turkish.toLowerCase().includes(query) ||
        (term.russian && term.russian.toLowerCase().includes(query)) ||
        (term.abbreviation && term.abbreviation.toLowerCase().includes(query)) ||
        (term.aliases && term.aliases.some((alias) => alias.toLowerCase().includes(query))) ||
        (term.description.tr && term.description.tr.toLowerCase().includes(query)) ||
        (term.description.en && term.description.en.toLowerCase().includes(query)) ||
        (term.description.ru && term.description.ru.toLowerCase().includes(query))
      return matchesCategory && matchesSearch
    })
  }, [searchQuery, selectedCategory, language, terms])

  const canonicalBaseUrl = 'https://thinklyze.com/glossary'
  const totalPages = Math.max(1, Math.ceil(filteredTerms.length / PAGE_SIZE))
  const paginatedTerms = filteredTerms.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, selectedCategory])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const structuredData = useMemo(() => {
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Digital Marketing Glossary',
      url: canonicalBaseUrl,
      itemListElement: terms.map((term, index) => {
        const termId = createTermId(term)
        const alternateNames = [
          term.turkish,
          term.abbreviation,
          ...(term.aliases || [])
        ].filter(Boolean)

        return {
          '@type': 'ListItem',
          position: index + 1,
          url: `${canonicalBaseUrl}#${termId}`,
          item: {
            '@type': 'DefinedTerm',
            name: term.english,
            description: term.description.en,
            inDefinedTermSet: canonicalBaseUrl,
            alternateName: alternateNames.length ? alternateNames : undefined
          }
        }
      })
    }
  }, [terms])

  const quickNavigationHighlights = new Set([
    'Return on Ad Spend',
    'Performance Max',
    'Customer Acquisition Cost',
    'Customer Data Platform',
    'Conversion Rate Optimization',
    'Churn Rate',
    'Net Promoter Score'
  ])
  const quickNavigationTerms = terms.filter((term) => quickNavigationHighlights.has(term.english))

  const handleQuickNavigation = (term: Term) => {
    const targetId = createTermId(term)
    const baseList =
      searchQuery || selectedCategory !== 'all' ? filteredTerms : terms
    const globalIndex = baseList.findIndex((t) => createTermId(t) === targetId)
    if (globalIndex !== -1) {
      const targetPage = Math.floor(globalIndex / PAGE_SIZE) + 1
      setCurrentPage(targetPage)
      setTimeout(() => {
        document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 120)
    }
  }

  const visibleQuickNavigation =
    searchQuery || selectedCategory !== 'all'
      ? filteredTerms.slice(0, Math.min(5, filteredTerms.length))
      : quickNavigationTerms

  return (
    <>
      <Helmet>
        <title>{language === 'tr' ? 'Dijital Pazarlama Sözlüğü - Thinklyze' : language === 'en' ? 'Digital Marketing Glossary - Thinklyze' : 'Глоссарий цифрового маркетинга - Thinklyze'}</title>
        <meta name="description" content={language === 'tr' ? 'Dijital pazarlama ve pazarlama terimlerinin kapsamlı sözlüğü. Türkçe ve İngilizce açıklamalar.' : language === 'en' ? 'Comprehensive glossary of digital marketing and marketing terms. Turkish and English explanations.' : 'Полный глоссарий терминов цифрового маркетинга. Объяснения на турецком и английском языках.'} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="pt-16 bg-white min-h-screen">
        {/* Hero Section */}
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium mb-6">
                <BookOpen className="w-4 h-4" />
                <span>{language === 'tr' ? 'Kapsamlı Sözlük' : language === 'en' ? 'Comprehensive Glossary' : 'Полный глоссарий'}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
                {language === 'tr' ? 'Dijital Pazarlama Sözlüğü' : language === 'en' ? 'Digital Marketing Glossary' : 'Глоссарий цифрового маркетинга'}
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                {language === 'tr' 
                  ? 'Dijital pazarlama ve pazarlama terimlerinin kapsamlı sözlüğü. Türkçe ve İngilizce açıklamalarla öğrenin.'
                  : language === 'en'
                  ? 'Comprehensive glossary of digital marketing and marketing terms. Learn with Turkish and English explanations.'
                  : 'Полный глоссарий терминов цифрового маркетинга и маркетинга. Изучайте с объяснениями на турецком и английском языках.'}
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="bg-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={language === 'tr' ? 'Terim ara... (Türkçe, İngilizce veya açıklama)' : language === 'en' ? 'Search term... (Turkish, English or description)' : 'Поиск термина... (турецкий, английский или описание)'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white"
                />
              </div>
            </div>

            {/* Category Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {categoryFilters.map((filter) => {
                const Icon = filter.icon
                const isActive = selectedCategory === filter.id
                return (
                  <button
                    key={filter.id}
                    onClick={() => setSelectedCategory(filter.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{filter.name[language as 'tr' | 'en' | 'ru'] || filter.name.en}</span>
                  </button>
                )
              })}
            </div>

            {/* Results Count */}
            <div className="mt-4 text-sm text-gray-600">
              {language === 'tr' 
                ? `${filteredTerms.length} terim bulundu`
                : language === 'en'
                ? `${filteredTerms.length} terms found`
                : `Найдено ${filteredTerms.length} терминов`}
            </div>

            {/* Quick Navigation */}
            {visibleQuickNavigation.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-gray-600 font-medium">
                  {language === 'tr' ? 'Popüler terimler:' : language === 'en' ? 'Popular terms:' : 'Популярные термины:'}
                </span>
                {visibleQuickNavigation.map((term) => (
                  <button
                    key={createTermId(term)}
                    type="button"
                    onClick={() => handleQuickNavigation(term)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    <span className="font-medium">
                      {language === 'tr' ? term.turkish : language === 'ru' ? (term.russian || term.english) : term.english}
                    </span>
                    {term.abbreviation && <span className="text-xs text-blue-500">{term.abbreviation}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Terms Grid */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredTerms.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  {language === 'tr' ? 'Sonuç bulunamadı' : language === 'en' ? 'No results found' : 'Результаты не найдены'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedTerms.map((term, index) => (
                  <div
                    key={createTermId(term)}
                    id={createTermId(term)}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow scroll-mt-24"
                  >
                    {/* English Title with Abbreviation */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {term.abbreviation ? (
                        <>
                          <span className="text-2xl">{term.abbreviation}</span>
                          <span className="text-lg ml-2 font-normal">({term.english})</span>
                        </>
                      ) : (
                        <span className="text-xl">{term.english}</span>
                      )}
                    </h3>

                    {/* Localized Title - Blue */}
                    <h4 className="text-base font-semibold text-blue-600 mb-3">
                      {language === 'ru' ? (term.russian || term.english) : language === 'tr' ? term.turkish : term.english}
                    </h4>

                    {/* Description */}
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {language === 'ru' 
                        ? (term.description.ru || term.description.en) 
                        : language === 'tr' 
                        ? term.description.tr 
                        : term.description.en}
                    </p>

                    {/* Formula if applicable */}
                    {term.formula && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 font-medium mb-1">
                          {language === 'tr' ? 'Formül:' : language === 'en' ? 'Formula:' : 'Формула:'}
                        </p>
                        <p className="text-sm text-gray-700 font-mono">
                          {language === 'ru' 
                            ? (term.formula.ru || term.formula.en)
                            : language === 'tr'
                            ? term.formula.tr
                            : term.formula.en}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {filteredTerms.length > 0 && totalPages > 1 && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    currentPage === 1
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {language === 'tr' ? 'Önceki' : language === 'en' ? 'Previous' : 'Назад'}
                </button>
                {Array.from({ length: totalPages }).map((_, idx) => {
                  const page = idx + 1
                  const isActive = page === currentPage
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => handlePageChange(page)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                        isActive ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                })}
                <button
                  type="button"
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    currentPage === totalPages
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {language === 'tr' ? 'Sonraki' : language === 'en' ? 'Next' : 'Вперёд'}
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  )
}

export default Glossary















